import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@unistocks/components/button/button';
import { FormFieldContainer } from '@unistocks/components/form/components/container';
import { FormIdProvider } from '@unistocks/components/form/components/form-id-context';
import { FormCheckbox } from '@unistocks/components/form/components/fields/form-checkbox';
import { FormDatepicker } from '@unistocks/components/form/components/fields/form-datepicker';
import { FormInput } from '@unistocks/components/form/components/fields/form-input';
import { FormRadioGroup } from '@unistocks/components/form/components/fields/form-radiogroup';
import { FormSelect } from '@unistocks/components/form/components/fields/form-select';
import { FormTypeahead } from '@unistocks/components/form/components/fields/form-typeahead';
import { FormSection } from '@unistocks/components/form/components/section';
import { t } from '@unistocks/shared/localization/index.ts';
import { isFunction } from '@unistocks/shared/utils/util';
import React, { useId, useImperativeHandle } from 'react';
import { FieldValues, FormProvider, Resolver, useForm, useFormState } from 'react-hook-form';
import { Form as TMForm, withStaticProperties } from 'tamagui';
import { z } from 'zod';
import { FormProps } from './model';

function FormInner<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
>({
  ref,
  children,
  formId: formIdProp,
  initialValues,
  shouldFocusError = true,
  schema,
  triggerText,
  triggerIcon,
  customTrigger,
  isLoading: isLoadingProp,
  disabled: disabledProp,
  onSubmit,
  onError,
  ...props
}: FormProps<TFieldValues, TContext, TTransformedValues>) {
  const autoId = useId();
  const formId = formIdProp ?? autoId;
  // Zod strips unknown keys by default. Preserve them to match the previous yup behaviour,
  // so values that are not part of the validation schema still reach the submit handler.
  const resolverSchema = schema instanceof z.ZodObject ? schema.loose() : schema;
  const form = useForm<TFieldValues, TContext, TTransformedValues>({
    mode: 'onSubmit',
    disabled: disabledProp,
    reValidateMode: 'onChange',
    shouldFocusError,
    defaultValues: initialValues,
    resolver: resolverSchema
      ? (zodResolver(resolverSchema as z.ZodType<FieldValues, FieldValues>) as unknown as Resolver<
          TFieldValues,
          TContext,
          TTransformedValues
        >)
      : undefined
  });

  const { handleSubmit, control } = form;
  // Subscribe through the hook instead of reading the `formState` proxy during render.
  const { isSubmitting: isSubmittingInternal, disabled } = useFormState({ control });
  const isSubmitting = isLoadingProp || isSubmittingInternal;
  const submit = handleSubmit(onSubmit, onError);

  useImperativeHandle(ref, () => form, [form]);

  const content = isFunction(children) ? children(form, submit) : children;

  return (
    <FormProvider {...form}>
      <FormIdProvider formId={formId}>
        <TMForm px={'$0.5'} {...props} id={formId} onSubmit={submit}>
          {content}

          <TMForm.Trigger asChild aria-busy={isSubmitting} alignItems={'center'}>
            {isFunction(customTrigger) ? (
              customTrigger(form, submit, isSubmitting)
            ) : React.isValidElement(customTrigger) ? (
              React.cloneElement(customTrigger, {
                isSubmitting,
                onSubmit: submit
              })
            ) : customTrigger === null ? null : (
              <Button
                type={'submit'}
                mt={'$3'}
                Icon={triggerIcon}
                loading={isSubmitting}
                disabled={disabled}
                $md={{ size: '$3', alignSelf: 'flex-end' }}
              >
                {triggerText ?? t('::Save')}
              </Button>
            )}
          </TMForm.Trigger>
        </TMForm>
      </FormIdProvider>
    </FormProvider>
  );
}

export const Form = withStaticProperties(FormInner, {
  FieldContainer: FormFieldContainer,
  Section: FormSection,

  Input: FormInput,
  Typeahead: FormTypeahead,
  Select: FormSelect,
  Datepicker: FormDatepicker,
  RadioGroup: FormRadioGroup,
  Checkbox: FormCheckbox
});
